import {
  Component,
  NgZone,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  InterviewAnswer,
  Question,
} from 'src/app/pages/virtual-interview/models/virtual-interview-model';

import { VideoRecoderService } from 'src/app/services/video-recorder/video-recoder.service';
import Swal from 'sweetalert2';
import { CompanyVirtualInterviewComponent } from '../../company-virtual-interview.component';
import { CompanyVrServiceService } from '../../service/company-vr-service.service';
import Theme from 'quill/core/theme';

@Component({
  selector: 'app-start-session',
  templateUrl: './start-session.component.html',
  styleUrls: ['./start-session.component.css'],
})
export class StartSessionComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('previewVideo') previewVideo!: ElementRef<HTMLVideoElement>;

  showTips: boolean = true;
  questions: Question[] = [];
  sessionId: string = '';
  interviewId: string = '';
  isLoadingQuestions: boolean = true;
  interviewTitle: string = 'Job Interview';

  interviewAnswers: InterviewAnswer[] = [];
  progressValueCurrentValue: number = 0;
  totalSeconds: number = 3600;
  initialTotalSeconds: number = 3600;
  secondsElapsed: number = 0;
  progressInterval: any;

  currentQuestionIndex = 0;
  isStartVr = false;
  isVideoOn = false;
  isMicOn = false;

  currentSessionIndex: number = 0;
  language: string = 'en';

  private subs: Subscription[] = [];

  constructor(
    public recorderService: VideoRecoderService,
    private zone: NgZone,
    private router: Router,

    private route: ActivatedRoute,
    private companyInterviewService: CompanyVrServiceService,
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.currentSessionIndex = navigation.extras.state['sessionIndex'] || 0;
    }
    this.interviewId = this.route.snapshot.params['id'];
  }

  // ─── Helper ───────────────────────────────────────────────────────────────
  isRTL(): boolean {
    return this.language === 'ar';
  }

  private t(en: string, ar: string): string {
    return this.isRTL() ? ar : en;
  }
  // ──────────────────────────────────────────────────────────────────────────

  ngAfterViewInit() {
    this.subs.push(
      this.recorderService.previewStream$.subscribe((stream) => {
        if (this.previewVideo) {
          this.previewVideo.nativeElement.srcObject = stream ?? null;
        }
      }),
    );
  }

  ngOnInit(): void {
    if (!this.interviewId) {
      Swal.fire({
        icon: 'error',
        title: this.t('Session Not Found', 'الجلسة غير موجودة'),
        text: this.t(
          'No session found. Redirecting...',
          'لم يتم العثور على جلسة. جارٍ إعادة التوجيه...',
        ),
        confirmButtonText: this.t('OK', 'حسناً'),
      }).then(() => {
        this.router.navigate(['/virtual-interview-steps']);
      });
      return;
    }

    this.companyInterviewService.joinInterview(this.interviewId).subscribe({
      next: (res: any) => {
        this.sessionId = res.session_id;
        this.loadSessionData();
      },
    });
    // this.loadSessionQuestions();

    this.subs.push(
      this.recorderService.isVideoOn$.subscribe((v) => (this.isVideoOn = v)),
      this.recorderService.isMicOn$.subscribe((v) => (this.isMicOn = v)),
    );
  }

  loadSessionData() {
    this.isLoadingQuestions = true;

    this.companyInterviewService.getSessionData(this.sessionId).subscribe({
      next: (sessionData) => {
        this.language = sessionData.language || 'en';
        this.interviewTitle = sessionData.interviewTitle;
        this.questions = sessionData.questionsInfo.map(
          (q: any, index: number) => ({
            text: q.interviewVideo?.questionText || `Question ${index + 1}`,
            explanation: undefined,
            hasExplanation: q.explained || false,
            explained: q.explained,
            interviewVideo: q.interviewVideo,
            recordedVideos: q.recordedVideos,
            remainingAdditionalAttempts:
              sessionData.remainingAdditionalAttempts,
            additionalAnswerDuration: sessionData.additionalAnswerDuration,
          }),
        );

        const remainingTime = sessionData.remainingSessionTime || 3600;
        this.totalSeconds = remainingTime;
        this.initialTotalSeconds = sessionData.totalSessionTime;
        this.secondsElapsed = this.initialTotalSeconds - remainingTime;
        this.progressValueCurrentValue =
          (this.secondsElapsed / this.initialTotalSeconds) * 100;

        this.interviewAnswers = this.questions.map((q: any, i: number) => ({
          index: i,
          question: q.text || '',
          videoBlob: null,
          videoUrl: null,
          duration: 0,
          answeredAt: null,
          recordedVideoId: null,
        }));

        this.isLoadingQuestions = false;
      },
      error: (error) => {
        Swal.fire({
          icon: 'error',
          title: this.t('Failed to Load Questions', 'فشل تحميل الأسئلة'),
          text: this.t(
            'Could not load interview questions. Please try again.',
            'تعذّر تحميل أسئلة المقابلة. يرجى المحاولة مجدداً.',
          ),
          confirmButtonText: this.t('OK', 'حسناً'),
        }).then(() => {
          this.isLoadingQuestions = false;
          this.router.navigate(['/virtual-interview-steps']);
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.stopProgressTimer();
    this.recorderService.finalizeAndCleanup();
  }

  toggleCamera() {
    this.recorderService.toggleVideoStream();
  }

  toggleMic() {
    this.recorderService.toggleAudioStream();
  }

  startInterview() {
    this.isStartVr = true;
    this.startProgressTimer();
  }

  startProgressTimer() {
    this.progressInterval = setInterval(() => {
      this.secondsElapsed++;
      this.progressValueCurrentValue =
        (this.secondsElapsed / this.initialTotalSeconds) * 100;

      if (this.progressValueCurrentValue >= 100) {
        this.progressValueCurrentValue = 100;
        this.stopProgressTimer();
        this.showTimeUpWarning();
      }
    }, 1000);
  }

  stopProgressTimer() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  showTimeUpWarning() {
    Swal.fire({
      icon: 'warning',
      title: this.t('Session Time Expired', 'انتهى وقت الجلسة'),
      text: this.t(
        'Your interview session time has ended. Please submit your answers.',
        'لقد انتهى وقت جلسة المقابلة. يرجى تسليم إجاباتك.',
      ),
      confirmButtonText: this.t('OK', 'حسناً'),
    });
  }

  handleNextQuestion(answerData: any) {
    this.zone.run(() => {
      const answer = this.interviewAnswers[this.currentQuestionIndex];
      answer.duration = answerData.duration;
      answer.videoBlob = answerData.videoBlob;
      answer.videoUrl = answerData.videoUrl;
      answer.answeredAt = new Date();
      answer.recordedVideoId = answerData.recordedVideoId;

      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        this.questions = [...this.questions];
      } else {
        this.finishInterview();
      }
    });
  }

  onCommentAdded() {
    this.questions.forEach((q: any) => {
      if (q.remainingAdditionalAttempts && q.remainingAdditionalAttempts > 0) {
        q.remainingAdditionalAttempts--;
      }
    });
  }

  finishInterview() {
    this.companyInterviewService.submitInterview(this.sessionId).subscribe({
      next: (response) => {
        const swalCommonOptions = {
          customClass: {
            confirmButton: 'swal-gradient-btn',
            icon: 'swal-custom-icon',
          },
          didOpen: () => {
            const icon = document.querySelector(
              '.swal2-icon.swal2-success .swal2-success-ring',
            ) as HTMLElement;
            if (icon) {
              icon.style.borderColor = '#db4d5a';
            }
            const checkmarks = document.querySelectorAll(
              '.swal2-icon.swal2-success [class^="swal2-success-line"]',
            ) as NodeListOf<HTMLElement>;
            checkmarks.forEach((el) => {
              el.style.backgroundColor = '#db4d5a';
            });
          },
        };

        if (this.currentSessionIndex === 0) {
          Swal.fire({
            ...swalCommonOptions,
            icon: 'success',
            title: this.t(
              'Responses Submitted Successfully!',
              'تم إرسال إجاباتك بنجاح!',
            ),
            html: this.isRTL()
              ? `
                  <style>
                    .swal-gradient-btn {
                      background: linear-gradient(90deg, #db4d5a 0%, #ed7054 100%) !important;
                      color: #fff !important;
                      border: none !important;
                      border-radius: 48px !important;
                      padding: 10px 30px !important;
                      font-weight: 600 !important;
                      font-size: 1rem !important;
                      cursor: pointer !important;
                      transition: opacity 0.3s ease !important;
                    }
                    .swal-gradient-btn:hover { opacity: 0.9 !important; }
                    .swal-msg p { margin: 6px 0; color: #555; font-size: 0.95rem; line-height: 1.6; }
                  </style>
                  <div class="swal-msg">
                    <p>✅ تم استلام إجاباتك وسيتم مراجعتها من قِبل فريق الشركة.</p>
                    <p>📋 سيتم التواصل معك من خلال الشركة بناءً على نتيجة المراجعة.</p>
                    <p>⏳ يُرجى متابعة بريدك الإلكتروني وإشعارات حسابك.</p>
                  </div>
                `
              : `
                  <style>
                    .swal-gradient-btn {
                      background: linear-gradient(90deg, #db4d5a 0%, #ed7054 100%) !important;
                      color: #fff !important;
                      border: none !important;
                      border-radius: 48px !important;
                      padding: 10px 30px !important;
                      font-weight: 600 !important;
                      font-size: 1rem !important;
                      cursor: pointer !important;
                      transition: opacity 0.3s ease !important;
                    }
                    .swal-gradient-btn:hover { opacity: 0.9 !important; }
                    .swal-msg p { margin: 6px 0; color: #555; font-size: 0.95rem; line-height: 1.6; }
                  </style>
                  <div class="swal-msg">
                    <p>✅ Your responses have been received and will be reviewed by the company's team.</p>
                    <p>📋 The company will reach out to you based on the review outcome.</p>
                    <p>⏳ Please keep an eye on your email and account notifications.</p>
                  </div>
                `,
            confirmButtonText: this.t('Got it, Thank you!', 'حسناً، شكراً!'),
          }).then(() => {
            this.router.navigate(['/']);
          });
        } else if (this.currentSessionIndex === 1) {
          Swal.fire({
            ...swalCommonOptions,
            icon: 'success',
            title: this.t('Interview Completed!', 'اكتملت مقابلتك بنجاح!'),
            html: this.isRTL()
              ? `
                  <style>
                    .swal-gradient-btn {
                      background: linear-gradient(90deg, #db4d5a 0%, #ed7054 100%) !important;
                      color: #fff !important;
                      border: none !important;
                      border-radius: 48px !important;
                      padding: 10px 30px !important;
                      font-weight: 600 !important;
                      font-size: 1rem !important;
                      cursor: pointer !important;
                      transition: opacity 0.3s ease !important;
                    }
                    .swal-gradient-btn:hover { opacity: 0.9 !important; }
                    .swal-msg p { margin: 6px 0; color: #555; font-size: 0.95rem; line-height: 1.6; }
                  </style>
                  <div class="swal-msg">
                    <p>🎉 أحسنت! لقد أتممت جميع مراحل المقابلة.</p>
                    <p>📋 سيتم التواصل معك من قِبل الشركة فور الانتهاء من مراجعة إجاباتك.</p>
                    <p>⏳ يُرجى متابعة بريدك الإلكتروني وإشعارات حسابك.</p>
                  </div>
                `
              : `
                  <style>
                    .swal-gradient-btn {
                      background: linear-gradient(90deg, #db4d5a 0%, #ed7054 100%) !important;
                      color: #fff !important;
                      border: none !important;
                      border-radius: 48px !important;
                      padding: 10px 30px !important;
                      font-weight: 600 !important;
                      font-size: 1rem !important;
                      cursor: pointer !important;
                      transition: opacity 0.3s ease !important;
                    }
                    .swal-gradient-btn:hover { opacity: 0.9 !important; }
                    .swal-msg p { margin: 6px 0; color: #555; font-size: 0.95rem; line-height: 1.6; }
                  </style>
                  <div class="swal-msg">
                    <p>🎉 Well done! You have completed all interview stages.</p>
                    <p>📋 The company will reach out to you once your responses have been reviewed.</p>
                    <p>⏳ Please keep an eye on your email and account notifications.</p>
                  </div>
                `,
            confirmButtonText: this.t('Got it, Thank you!', 'حسناً، شكراً!'),
          }).then(() => {
            this.router.navigate(['/']);
          });
        } else {
          this.router.navigate(['/']);
        }
      },
      error: (error) => {
        Swal.fire({
          icon: 'error',
          title: this.t('Submission Failed', 'فشل التسليم'),
          text: this.t(
            'Failed to submit interview. Please try again.',
            'فشل تسليم المقابلة. يرجى المحاولة مجدداً.',
          ),
          confirmButtonText: this.t('OK', 'حسناً'),
          customClass: {
            confirmButton: 'swal-gradient-btn',
          },
        });
      },
    });
  }

  formatTimeRemaining(seconds: number): string {
    return `${seconds}s`;
  }

  getFormattedRemainingTime(): string {
    const remainingSeconds = this.initialTotalSeconds - this.secondsElapsed;
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
